/**
 * Quote Validation Rules
 * Centralized validation logic for quote wizard navigation
 */

import { STEP_IDS } from './steps';

/**
 * Validation error messages
 */
export const validationMessages = {
  customerTypeRequired: '⚠️ Customer type is required.\n\nPlease select whether this is a Prospect or Existing Customer in Deal Info before proceeding.',
  customerNameRequired: '⚠️ Customer name is required.\n\nPlease enter the customer name in Deal Info before proceeding.',
  industryRequired: '⚠️ Industry is required.\n\nPlease select the customer industry in Deal Info before proceeding.',
  dealTypeRequired: '⚠️ Solution area is required.\n\nPlease select the deal type (HCM, Finance, SCM, CRM, or Cross-Domain) in Deal Info before proceeding.',
  employeeCountRequired: '⚠️ Employee count is required for HCM deals.\n\nPlease enter the number of employees in Deal Info before proceeding.',
  opportunityIdRequired: '⚠️ Salesforce Opportunity ID is required.\n\nPlease enter a valid 18-character Salesforce Opportunity ID (starts with 006).',
  opportunityIdInvalid: '⚠️ Invalid Salesforce Opportunity ID format.\n\nMust be 18 characters and start with "006" (e.g., 006PE00000OHoJ5YAL).',
  contactNameRequired: '⚠️ Primary contact name is required.\n\nPlease enter the contact name in Deal Info. This appears on the customer quote.',
  contactEmailRequired: '⚠️ Contact email is required.\n\nPlease enter the contact email in Deal Info. This appears on the customer quote.',
  salesRepRequired: '⚠️ Sales rep is required.\n\nPlease enter the sales rep name in Deal Info. This appears on all quotes.',
  platformRequired: '⚠️ Platform selection required.\n\nPlease select at least one platform (SplashBI or SplashAI) before proceeding.',
  deploymentRequired: '⚠️ Deployment type required.\n\nPlease select a deployment model before proceeding.',
  productRequired: '⚠️ Product selection required.\n\nYou must select at least ONE product before proceeding:\n• Data Pipeline, OR\n• HCM Module, OR\n• Finance Module, OR\n• SCM Module, OR\n• CRM Module'
};

/**
 * Validate Salesforce Opportunity ID format
 * @param {string} id - Opportunity ID to validate
 * @returns {boolean} True if valid format
 */
export const isValidOpportunityId = (id) => {
  if (!id || typeof id !== 'string') return false;
  const trimmed = id.trim();
  // Must be 18 characters and start with 006
  return trimmed.length === 18 && trimmed.startsWith('006');
};

/**
 * Check if quote has any products selected
 * @param {Object} quoteData - Current quote data
 * @returns {boolean} True if at least one product is selected
 */
export const hasProducts = (quoteData) => {
  return (
    (quoteData.reportsEBS?.length || 0) > 0 ||
    (quoteData.reportsCloud?.length || 0) > 0 ||
    (quoteData.peopleAnalytics?.length || 0) > 0 ||
    (quoteData.financialAnalytics?.length || 0) > 0 ||
    (quoteData.dataPipelines?.length || 0) > 0 ||
    (quoteData.utilities?.length || 0) > 0 ||
    quoteData.glConnectOnPremise ||
    quoteData.glConnectCloud
  );
};

/**
 * Check if deal type requires employee count
 * @param {string} dealType - Deal type value
 * @returns {boolean} True if employee count is required
 */
export const requiresEmployeeCount = (dealType) => {
  return dealType === 'HCM' || dealType === 'Cross-Domain';
};

/**
 * Validate if user can navigate to a specific step
 * @param {number} targetStep - Target step index
 * @param {number} currentStep - Current step index
 * @param {Object} quoteData - Current quote data
 * @returns {boolean} True if navigation is allowed
 */
export const canNavigateToStep = (targetStep, currentStep, quoteData) => {
  // Can always go back
  if (targetStep <= currentStep) return true;

  // Step 1+ requires all Deal Info fields
  if (targetStep > STEP_IDS.DEAL_INFO) {
    // Salesforce Opportunity ID
    if (!isValidOpportunityId(quoteData.opportunityId)) return false;
    // Customer Info
    if (!quoteData.customerType) return false;
    if (!quoteData.customerName?.trim()) return false;
    if (!quoteData.industry) return false;
    // Primary Contact
    if (!quoteData.contactName?.trim()) return false;
    if (!quoteData.contactEmail?.trim()) return false;
    // Sales Rep
    if (!quoteData.salesRep?.trim()) return false;
    // Deal Type & Employee Count
    if (!quoteData.dealType) return false;
    if (requiresEmployeeCount(quoteData.dealType) && quoteData.employeeCount === 0) {
      return false;
    }
  }

  // Step 2+ requires platform and deployment
  if (targetStep > STEP_IDS.PLATFORM) {
    if (!quoteData.hasSplashBI && !quoteData.hasSplashAI) return false;
    if (!quoteData.deployment) return false;
  }

  // Step 4+ requires at least one product
  if (targetStep > STEP_IDS.PRODUCTS) {
    if (!hasProducts(quoteData)) return false;
  }

  return true;
};

/**
 * Get validation error message for failed navigation
 * @param {number} targetStep - Target step index
 * @param {Object} quoteData - Current quote data
 * @returns {string|null} Error message or null if valid
 */
export const getValidationError = (targetStep, quoteData) => {
  // Deal Info validations
  if (!quoteData.opportunityId?.trim()) {
    return validationMessages.opportunityIdRequired;
  }

  if (!isValidOpportunityId(quoteData.opportunityId)) {
    return validationMessages.opportunityIdInvalid;
  }

  if (!quoteData.customerType) {
    return validationMessages.customerTypeRequired;
  }

  if (!quoteData.customerName?.trim()) {
    return validationMessages.customerNameRequired;
  }

  if (!quoteData.industry) {
    return validationMessages.industryRequired;
  }

  if (!quoteData.contactName?.trim()) {
    return validationMessages.contactNameRequired;
  }

  if (!quoteData.contactEmail?.trim()) {
    return validationMessages.contactEmailRequired;
  }

  if (!quoteData.salesRep?.trim()) {
    return validationMessages.salesRepRequired;
  }

  if (!quoteData.dealType) {
    return validationMessages.dealTypeRequired;
  }

  if (requiresEmployeeCount(quoteData.dealType) && quoteData.employeeCount === 0) {
    return validationMessages.employeeCountRequired;
  }

  // Platform validations
  if (targetStep > STEP_IDS.PLATFORM) {
    if (!quoteData.hasSplashBI && !quoteData.hasSplashAI) {
      return validationMessages.platformRequired;
    }
    if (!quoteData.deployment) {
      return validationMessages.deploymentRequired;
    }
  }

  // Product validations
  if (targetStep > STEP_IDS.PRODUCTS && !hasProducts(quoteData)) {
    return validationMessages.productRequired;
  }

  return null;
};

/**
 * Validate entire quote for final submission
 * @param {Object} quoteData - Current quote data
 * @returns {{ valid: boolean, errors: string[] }} Validation result
 */
export const validateQuoteForSubmission = (quoteData) => {
  const errors = [];

  // Salesforce Opportunity ID - Required and must be valid format
  if (!quoteData.opportunityId?.trim()) {
    errors.push('Salesforce Opportunity ID is required');
  } else if (!isValidOpportunityId(quoteData.opportunityId)) {
    errors.push('Invalid Salesforce Opportunity ID format (must be 18 chars, start with 006)');
  }

  // Deal Info validations
  if (!quoteData.customerType) {
    errors.push('Customer type is required');
  }

  if (!quoteData.customerName?.trim()) {
    errors.push('Customer name is required');
  }

  if (!quoteData.industry) {
    errors.push('Industry is required');
  }

  if (!quoteData.dealType) {
    errors.push('Deal type / Solution area is required');
  }

  if (requiresEmployeeCount(quoteData.dealType) && quoteData.employeeCount === 0) {
    errors.push('Employee count is required for HCM deals');
  }

  // Contact & Sales Rep - Required for quote generation
  if (!quoteData.contactName?.trim()) {
    errors.push('Primary contact name is required');
  }

  if (!quoteData.contactEmail?.trim()) {
    errors.push('Contact email is required');
  }

  if (!quoteData.salesRep?.trim()) {
    errors.push('Sales rep name is required');
  }

  // Platform validations
  if (!quoteData.hasSplashBI && !quoteData.hasSplashAI) {
    errors.push('At least one platform must be selected');
  }

  if (!quoteData.deployment) {
    errors.push('Deployment type is required');
  }

  // Product validation
  if (!hasProducts(quoteData)) {
    errors.push('At least one product must be selected');
  }

  // Note: Contract start date is optional - quote creation date is recorded automatically on submit

  return {
    valid: errors.length === 0,
    errors
  };
};

export default {
  canNavigateToStep,
  getValidationError,
  validateQuoteForSubmission,
  hasProducts,
  requiresEmployeeCount,
  validationMessages
};
